<template>
    <kgr-form-scaffold
        :with-primary-buttons="false"
        @backBtnClicked="redirectToOverview"
    >
        <div class="animated fadeIn">
            <div class="kgr-row kgr-text--rightAligned">
                <a
                    class="kgr-button kgr-button--accent"
                    @click="openCreateDialog"
                >
                    {{ $t('Neue Beitragszuweisung anlegen') }}
                </a>
            </div>
            <div class="kgr-row">
                <kgr-checkbox
                    v-model="filter.showInactive"
                    :label="$t('Inaktive Beiträge anzeigen')"
                />
            </div>
            <table class="kgr-table kgr-table--compact kgr-table--block kgr-table--striped printable">
                <thead>
                    <tr class="kgr-table__row">
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Beitrag') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Kategorie') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Abteilung') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Rechnungsempfänger') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Preis') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Anzahl') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Vergünstigung') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('lädt Guthaben auf') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('mit Guthaben zahlbar') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Aktuelle Zahlungsart') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Start') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Ende') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions kgr-hidden-print"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="loading">
                        <td
                            colspan="9"
                            style="text-align: center;"
                        >
                            <kgr-loader />
                        </td>
                    </tr>
                    <tr v-else-if="filteredFees.length === 0">
                        <td
                            colspan="9"
                            style="text-align: center;"
                        >
                            {{ $t('dataTable.noData') }}
                        </td>
                    </tr>
                    <template v-else>
                        <tr
                            v-for="fee in filteredFees"
                            :key="fee.id"
                            class="kgr-table__row"
                            :class="{'is-inactive': fee.archived}"
                        >
                            <td class="kgr-table__cell">
                                {{ fee.clubFee }}
                                <kgr-icon
                                    v-if="fee.note !== ''"
                                    :title="$t('Für diesen Beitrag gibt es einen zusätzlichen Rechnungshinweis')"
                                    icon-class="info-circle"
                                    small
                                    light
                                    disabled
                                />
                                <button
                                    v-if="!fee.archived"
                                    class="kgr-table__action"
                                    @click="openNoteDialog(fee)"
                                >
                                    <kgr-icon
                                        icon-class="edit"
                                        small
                                    />
                                </button>
                            </td>
                            <td class="kgr-table__cell">
                                {{ fee.clubFeeCategory }}
                            </td>
                            <td class="kgr-table__cell">
                                {{ fee.department }}
                            </td>
                            <td class="kgr-table__cell">
                                <a
                                    v-if="fee.invoiceRecipient.id !== id"
                                    class="kgr-link"
                                    :href="'/backend/clubs/members/' + fee.invoiceRecipient.id +'/accounting/fee-assignment'"
                                >
                                    {{ fee.invoiceRecipient.name }}
                                </a>
                                <template v-if="fee.invoiceRecipient.id === id">
                                    {{ fee.invoiceRecipient.name }}
                                </template>
                            </td>
                            <td class="kgr-table__cell">
                                <template v-if="!fee.discount || fee.discount.type !== 'fixed_price'">
                                    {{ getPricePerTime(fee.pricePerTime) }}
                                </template>
                                <template v-if="fee.discount && fee.discount.type === 'fixed_price'">
                                    <div style="text-decoration: line-through">
                                        {{ getPricePerTime(fee.pricePerTime) }}
                                    </div>
                                    <div>{{ getDiscountedPricePerTime(fee.pricePerTime, fee.discount.price) }}</div>
                                </template>
                            </td>
                            <td class="kgr-table__cell">
                                {{ getQuantityFactor(fee.quantityFactor) }}
                            </td>
                            <td class="kgr-table__cell">
                                <template v-if="fee.discount && fee.discount.type === 'percentage'">
                                    {{ fee.discount.name }}
                                </template>
                                <template v-if="fee.discount && fee.discount.type === 'fixed_price'">
                                    {{ $t('individueller Preis') }}
                                </template>
                                <template v-if="!fee.discount">
                                    {{ $t('nein') }}
                                </template>
                            </td>
                            <td class="kgr-table__cell">
                                <template v-if="fee.chargeCredit">
                                    {{ $t('ja') }}
                                </template>
                                <template v-if="!fee.chargeCredit">
                                    {{ $t('nein') }}
                                </template>
                            </td>
                            <td class="kgr-table__cell">
                                <template v-if="fee.settleWithCredit">
                                    {{ $t('ja') }}
                                </template>
                                <template v-if="!fee.settleWithCredit">
                                    {{ $t('nein') }}
                                </template>
                                <button
                                    v-if="!fee.archived"
                                    class="kgr-table__action"
                                    @click="openCreditDialog(fee)"
                                >
                                    <kgr-icon
                                        icon-class="edit"
                                        small
                                    />
                                </button>
                            </td>
                            <td class="kgr-table__cell">
                                {{ getPaymentMethod(fee) }}
                                <button
                                    class="kgr-table__action"
                                    @click="openPaymentHistoryDialog(fee)"
                                >
                                    <kgr-icon
                                        icon-class="edit"
                                        small
                                    />
                                </button>
                            </td>
                            <td class="kgr-table__cell">
                                {{ formatDate(fee.runTime.start) }}
                            </td>
                            <td class="kgr-table__cell">
                                {{ formatDate(fee.runTime.end) }}
                            </td>
                            <td class="kgr-table__cell kgr-table__cell--actions kgr-hidden-print">
                                <button
                                    v-if="!fee.archived"
                                    class="kgr-table__action"
                                    @click="openRuntimeDialog(fee)"
                                >
                                    <kgr-icon icon-class="edit" />
                                </button>
                                <button
                                    v-if="!fee.archived"
                                    class="kgr-table__action"
                                    @click="deleteMemberClubFee(fee.id)"
                                >
                                    <kgr-icon icon-class="delete" />
                                </button>
                                <button
                                    class="kgr-table__action"
                                    @click="toggleArchived(fee.id)"
                                >
                                    <kgr-icon :icon-class="fee.archived ? 'unarchive' : 'archive'" />
                                </button>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <kgr-lightbox
                v-if="modalFee && showCreditDialog"
                :value="showCreditDialog"
                :headline="modalFee.clubFee"
                animated
                @post-input="closeCreditDialog"
            >
                <div>
                    <kgr-form-row
                        :label="$t('Mit Guthaben zahlbar')"
                        :error="error"
                        field-name="settleWithCredit"
                    >
                        <kgr-checkbox
                            v-model="modalFee.settleWithCredit"
                        />
                    </kgr-form-row>
                    <hr class="kgr-separator--light" />
                    <div class="kgr-row">
                        <button
                            class="kgr-button"
                            @click="closeCreditDialog"
                        >
                            {{ $t('cancel') }}
                        </button>
                        <button
                            class="kgr-button kgr-button--accent"
                            @click="updateMemberClubFee(modalFee)"
                        >
                            {{ $t('save') }}
                        </button>
                    </div>
                </div>
            </kgr-lightbox>
            <kgr-lightbox
                v-if="modalFee && showRuntimeDialog"
                :headline="modalFee.clubFee"
                :value="showRuntimeDialog"
                animated
                @post-input="closeRuntimeDialog"
            >
                <div>
                    <kgr-form-row
                        :label="$t('Start')"
                        :error="error"
                        field-name="runTime"
                    >
                        <kgr-date-picker
                            v-model="modalFee.runTime.start"
                        />
                    </kgr-form-row>
                    <kgr-form-row
                        :label="$t('Ende')"
                        :error="error"
                        field-name="runTimeEnd"
                    >
                        <kgr-date-picker
                            v-model="modalFee.runTime.end"
                        />
                    </kgr-form-row>
                    <hr class="kgr-separator--light" />
                    <div class="kgr-row">
                        <button
                            class="kgr-button"
                            @click="closeRuntimeDialog"
                        >
                            {{ $t('cancel') }}
                        </button>
                        <button
                            class="kgr-button kgr-button--accent"
                            @click="updateMemberClubFee(modalFee)"
                        >
                            {{ $t('save') }}
                        </button>
                    </div>
                </div>
            </kgr-lightbox>
            <kgr-lightbox
                v-if="modalFee && showNoteDialog"
                :value="showNoteDialog"
                :headline="modalFee.clubFee"
                animated
                @post-input="closeNoteDialog"
            >
                <div>
                    <kgr-form-row
                        :label="$t('Zusätzlicher Rechnungshinweis am Beitrag')"
                        :error="error"
                        field-name="note"
                    >
                        <kgr-text-input
                            v-model="modalFee.note"
                            :max-length="255"
                        />
                    </kgr-form-row>
                    <hr class="kgr-separator--light" />
                    <div class="kgr-row">
                        <button
                            class="kgr-button"
                            @click="closeNoteDialog"
                        >
                            {{ $t('cancel') }}
                        </button>
                        <button
                            class="kgr-button kgr-button--accent"
                            @click="updateMemberClubFee(modalFee)"
                        >
                            {{ $t('save') }}
                        </button>
                    </div>
                </div>
            </kgr-lightbox>
            <kgr-lightbox
                v-if="newMemberClubFee && showCreateDialog"
                :value="showCreateDialog"
                :headline="$t('Neue Beitragszuweisung anlegen')"
                scrollable
                @post-input="closeCreateDialog"
            >
                <div>
                    <kgr-form-row
                        :error="error"
                        required
                        field-name="clubFeeId"
                    >
                        <kgr-club-fee-select
                            v-model="newMemberClubFee.clubFee"
                            :club-id="clubId"
                            @post-input="onClubFeeChanged"
                        />
                    </kgr-form-row>
                    <kgr-form-row
                        :label="$t('Zusätzlicher Rechnungshinweis am Beitrag')"
                        :error="error"
                        field-name="note"
                    >
                        <kgr-text-input
                            v-model="newMemberClubFee.note"
                            :max-length="255"
                        />
                    </kgr-form-row>
                    <kgr-form-row
                        :error="error"
                        field-name="invoiceRecipient"
                        required
                        :label="$t('Rechnungsempfänger')"
                    >
                        <kgr-member-relation-select
                            v-model="newMemberClubFee.invoiceRecipient"
                            :member-id="id"
                            :default-member-id="defaultInvoiceReceiver"
                            @post-input="loadDefaultPaymentSettingsOfInvoiceReceiver"
                        />
                    </kgr-form-row>
                    <kgr-form-row
                        :label="$t('Standard-Zahlart')"
                        :error="error"
                        field-name="paymentMethod"
                        required
                        :tooltip="newMemberClubFee.paymentMethod && newMemberClubFee.paymentMethod.value === 'debit-card' ? $t('Verwendung nur über externes Zahlungsterminal möglich') : ''"
                    >
                        <kgr-member-payment-method-select
                            v-model="newMemberClubFee.paymentMethod"
                            :sepa-disabled="!getSepaMandateAvailableForInvoiceReceiver"
                            :default-payment-method="defaultPaymentMethodOfInvoiceReceiver"
                        />
                    </kgr-form-row>
                    <kgr-form-row
                        v-if="newMemberClubFee.invoiceRecipient && newMemberClubFee.paymentMethod && newMemberClubFee.paymentMethod.value === 'sepa-debit'"
                        :label="$t('Sepa-Mandat')"
                        :error="error"
                        field-name="paymentMethod"
                    >
                        <kgr-member-sepa-debit-select
                            v-model="newMemberClubFee.sepaDebit"
                            :member-id="newMemberClubFee.invoiceRecipient.id"
                        />
                    </kgr-form-row>
                    <div class="kgr-row kgr-inputGroup">
                        <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
                            <div class="kgr-grid__cell">
                                <kgr-form-row
                                    :label="$t('Start')"
                                    :error="error"
                                    required
                                    field-name="runTime"
                                >
                                    <kgr-date-picker
                                        v-model="newMemberClubFee.start"
                                    />
                                </kgr-form-row>
                            </div>
                            <div class="kgr-grid__cell">
                                <kgr-form-row
                                    v-if="!isNewClubFeeOneTime"
                                    :label="$t('Ende')"
                                    :error="error"
                                    field-name="end"
                                >
                                    <kgr-date-picker
                                        v-model="newMemberClubFee.end"
                                    />
                                </kgr-form-row>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="newMemberClubFee.clubFee && newMemberClubFee.clubFee.rollingIntervalType !== null"
                        class="kgr-alert kgr-alert--prominentInfo kgr-row"
                    >
                        {{ $t('Dieser Beitrag wird als rollierende Jahresgebühr mit dem oben ausgewähltem Startdatum angelegt.') }}
                    </div>
                    <kgr-form-row
                        v-if="isNewClubFeeOneTime"
                        :label="$t('Anzahl')"
                        :error="error"
                        field-name="quantityFactor"
                    >
                        <kgr-generic-select
                            v-model="newMemberClubFee.quantityFactor"
                            no-null-option
                            :options="quantityFactorSelect"
                            :disabled="!isNewClubFeeOneTime"
                        />
                    </kgr-form-row>
                    <kgr-form-row
                        :label="$t('Vergünstigung')"
                        :error="error"
                        field-name="discount"
                    >
                        <kgr-generic-select
                            v-model="newMemberClubFee.discount"
                            :options="discounts"
                            display="name"
                        />
                    </kgr-form-row>
                    <kgr-form-row
                        v-if="newMemberClubFee.discount && newMemberClubFee.discount.id === 'individual'"
                        :label="$t('Individueller Preis')"
                        :error="error"
                        :tooltip="'Steuersatz ' + (fixedPriceDiscountVat || '_') + ' %'"
                        field-name="individualPrice"
                    >
                        <kgr-number-input
                            v-model="newMemberClubFee.fixedPriceDiscount.amount"
                        />
                    </kgr-form-row>
                    <kgr-form-row
                        v-if="newMemberClubFee.discount && newMemberClubFee.discount.id === 'individual'"
                        :error="error"
                        field-name="netOrGross"
                    >
                        <kgr-generic-radio
                            v-model="newMemberClubFee.fixedPriceDiscount.netOrGross"
                            :options="netOrGrossOptions"
                        />
                    </kgr-form-row>
                    <kgr-form-row
                        :error="error"
                        field-name="settleWithCredit"
                    >
                        <kgr-checkbox
                            v-model="newMemberClubFee.settleWithCredit"
                            :disabled="newMemberClubFee.clubFee && newMemberClubFee.clubFee.chargeCredit"
                            :label="$t('Mit Guthaben zahlbar')"
                        />
                    </kgr-form-row>
                    <hr class="kgr-separator--light" />
                    <div class="kgr-row">
                        <button
                            class="kgr-button"
                            @click="closeCreateDialog"
                        >
                            {{ $t('cancel') }}
                        </button>
                        <button
                            class="kgr-button kgr-button--accent"
                            @click="createMemberClubFee"
                        >
                            {{ $t('save') }}
                        </button>
                    </div>
                </div>
            </kgr-lightbox>
            <kgr-member-payment-history-dialog
                v-if="modalFee && showPaymentHistoryDialog"
                :id="modalFee.id"
                :show="showPaymentHistoryDialog"
                :member-id="modalFee.invoiceRecipient.id"
                @post-input="closePaymentHistoryDialog"
                @close="closePaymentHistoryDialog"
            />
        </div>
    </kgr-form-scaffold>
</template>

<script>
import { format, isValid, parse, parseISO, startOfMonth } from '@legacy/js/date/date-fns.js';
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue';
import ClubFeeSelect from '../../../components/common/form/select/ClubFeeSelect.vue';
import DatePicker from '../../../components/common/form/picker/DatePicker.vue';
import Error from '../../../js/model/Error.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import FormScaffold from '../../../components/common/form/FormScaffold.vue';
import GenericRadio from '../../../components/common/form/radio/GenericRadio.vue';
import GenericSelect from '../../../components/common/form/select/GenericSelect.vue';
import Helper from '../../../js/accounting/Helper.js';
import Icon from '../../../components/common/icon/Icon.vue';
import Lightbox from '../../../components/common/modal/Lightbox.vue';
import Loading from '../../../components/common/Loading.vue';
import MemberClubFeeRepository from '../../../js/repository/MemberClubFeeRepository.js';
import MemberPaymentHistoryDialog from '../../../components/members/accounting/PaymentHistoryDialog.vue';
import MemberPaymentMethodSelect from '../../../components/common/form/select/MemberPaymentMethodSelect.vue';
import MemberRelationSelect from '../../../components/common/form/select/MemberRelationSelect.vue';
import MemberSepaDebitSelect from '../../../components/common/form/select/MemberSepaDebitSelect.vue';
import MemberSettingsRepository from '../../../js/repository/MemberSettingsRepository.js';
import NumberInput from '../../../components/common/form/input/NumberInput.vue';
import TextInput from '../../../components/common/form/input/TextInput.vue';
import axios from 'axios';

export default {
    components: {
        'kgr-loader': Loading,
        'kgr-lightbox': Lightbox,
        'kgr-checkbox': Checkbox,
        'kgr-icon': Icon,
        'kgr-date-picker': DatePicker,
        'kgr-generic-select': GenericSelect,
        'kgr-generic-radio': GenericRadio,
        'kgr-club-fee-select': ClubFeeSelect,
        'kgr-member-relation-select': MemberRelationSelect,
        'kgr-member-payment-method-select': MemberPaymentMethodSelect,
        'kgr-member-sepa-debit-select': MemberSepaDebitSelect,
        'kgr-member-payment-history-dialog': MemberPaymentHistoryDialog,
        'kgr-form-row': FormRow,
        'kgr-form-scaffold': FormScaffold,
        'kgr-text-input': TextInput,
        'kgr-number-input': NumberInput,
    },
    props: {
        id: {
            type: Number,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        }
    },
    data() {
        return {
            error: new Error(),
            loading: false,
            initialLoading: true,
            showCreditDialog: false,
            showRuntimeDialog: false,
            showPaymentHistoryDialog: false,
            showNoteDialog: false,
            showCreateDialog: false,
            quantityFactorSelect: [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20],
            netOrGrossOptions: {
                'Brutto': 'gross',
                'Netto': 'net',
            },
            memberClubFees: [],
            discounts: [],
            newMemberClubFee: null,
            modalFee: null,
            defaultInvoiceReceiver: null,
            filter: {
                showInactive: true,
            },
            defaultPaymentMethodOfInvoiceReceiver: 'indefinite',
            sepaMandateAvailableForInvoiceReceiver: null,
        }
    },
    computed: {
        fixedPriceDiscountVat() {
            if(null !== this.newMemberClubFee.clubFee) {
                let vat = this.newMemberClubFee.clubFee.basePrice.vat;
                return (vat.numerator / vat.denominator * 100).toFixed(2);
            }
            return null;
        },
        filterText() {
            return this.filter.showInactive ? this.$t('Alle Mitgliedsbeiträge werden angezeigt') : this.$t('Inaktive Mitgliedsbeiträge sind ausgeblendet');
        },
        isNewClubFeeOneTime() {
            return null !== this.newMemberClubFee.clubFee && this.newMemberClubFee.clubFee.basePrice.type === 'oneTime'
        },
        filteredFees() {
            let fees = this.memberClubFees;
            if (!this.filter.showInactive) {
                let today = new Date(new Date().toISOString().slice(0, 10));
                fees = fees.filter(memberClubFee => {
                    return !memberClubFee.archived &&
                            (memberClubFee.runTime.end == null || new Date(memberClubFee.runTime.end) >= today);
                });
            }

            return fees;
        },
        getSepaMandateAvailableForInvoiceReceiver() {
            if (this.sepaMandateAvailableForInvoiceReceiver === null) {
                this.loadDefaultPaymentSettingsOfInvoiceReceiver();
            }
            return this.sepaMandateAvailableForInvoiceReceiver;
        }
    },
    created() {
        this.loadMemberClubFees();
        this.initNew();
    },
    methods: {
        onClubFeeChanged(clubFee) {
            this.newMemberClubFee.note = clubFee.note || '';
        },
        loadMemberClubFees() {
            this.loading = true;
            MemberClubFeeRepository.findByMemberAndDefaultContext(this.id)
                .then(response => this.memberClubFees = response)
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => {
                    this.loading = false;
                    this.initialLoading = false;
                });
        },
        loadMemberSettings() {
            MemberSettingsRepository.findByMember(this.id)
                .then(memberSettings => {
                    this.defaultInvoiceReceiver = memberSettings.defaultInvoiceReceiver;
                    this.defaultPaymentMethod =  memberSettings.defaultPaymentMethod;
                    if (this.newMemberClubFee) {

                        this.newMemberClubFee.paymentMethod = this.defaultPaymentMethodOfInvoiceReceiver !== 'other-invoice-receiver' && this.defaultPaymentMethodOfInvoiceReceiver !== null ?
                            this.defaultPaymentMethodOfInvoiceReceiver :
                            'indefinite';

                        this.newMemberClubFee.sepaDebit = memberSettings.currentDefaultSepa;
                    }
                    if (this.newMemberClubFee && this.defaultInvoiceReceiver) {
                        this.newMemberClubFee.invoiceRecipient = this.defaultInvoiceReceiver;
                        this.loadDefaultPaymentSettingsOfInvoiceReceiver();
                    }
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        loadDefaultPaymentSettingsOfInvoiceReceiver() {
            if(!this.newMemberClubFee.invoiceRecipient?.id) {
                return;
            }
            MemberSettingsRepository.findByMember(this.newMemberClubFee.invoiceRecipient.id)
                .then(memberSettings => {
                    this.sepaMandateAvailableForInvoiceReceiver = memberSettings.sepaMandateAvailable;
                    this.newMemberClubFee.sepaDebit = memberSettings.currentDefaultSepa;
                    this.defaultPaymentMethodOfInvoiceReceiver =  memberSettings.defaultPaymentMethod;
                    this.newMemberClubFee.paymentMethod = this.defaultPaymentMethodOfInvoiceReceiver !== 'other-invoice-receiver' && this.defaultPaymentMethodOfInvoiceReceiver !== null ?
                        this.defaultPaymentMethodOfInvoiceReceiver :
                        'indefinite';
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
        createMemberClubFee() {
            axios
                .post('/club/members/' + this.id + '/club-fees', {
                    'clubFeeId': this.newMemberClubFee.clubFee?.id,
                    'note': this.newMemberClubFee.note,
                    'settleWithCredit': this.newMemberClubFee.settleWithCredit,
                    'invoiceRecipient': this.newMemberClubFee.invoiceRecipient?.id,
                    'quantityFactor': {
                        'numerator': this.newMemberClubFee.quantityFactor,
                        'denominator': 1
                    },
                    'percentageDiscount': this.newMemberClubFee.discount?.id === 'individual' ? null : this.newMemberClubFee.discount?.id,
                    'fixedPriceDiscount': this.newMemberClubFee.discount?.id === 'individual' ? {
                        'amount': {
                            'numerator': Math.round(this.newMemberClubFee.fixedPriceDiscount.amount * 100),
                            'denominator': 100,
                        },
                        'vat': {
                            'numerator': this.fixedPriceDiscountVat * 100,
                            'denominator': 10000,
                        },
                        'netOrGross': this.newMemberClubFee.fixedPriceDiscount.netOrGross,
                        'currency': this.newMemberClubFee.clubFee ? this.newMemberClubFee.clubFee.basePrice.currency : null,
                    } : null,
                    'runTime': {
                        'start': this.getApiDateFromString(this.newMemberClubFee.start),
                        'end': this.getApiDateFromString(this.newMemberClubFee.end),
                    },
                    'paymentMethod': {
                        type: this.newMemberClubFee.paymentMethod.value,
                        id: this.newMemberClubFee.sepaDebit?.id
                    }
                })
                .then(() => {
                    this.loadMemberClubFees();
                    this.closeModals();
                })
                .catch((error) => this.error = Error.fromResponse(error.response));

        },
        deleteMemberClubFee(id) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst du den Beitrag wirklich löschen?'), () => {
                axios
                    .delete('/club/member-club-fees/' + id)
                    .then(() => this.loadMemberClubFees())
                    .catch(() => this.$notify.error('saveChanges.error'));
            }, this.$t('Löschen'));
        },
        toggleArchived(id) {
            const isArchived = this.memberClubFees.find(fee => fee.id === id).archived;
            const buttonText = isArchived ? this.$t('Archivierung aufheben') : this.$t('Archivieren');
            const message = isArchived ? this.$t('Willst du die Archivierung des Beitrags wirklich aufheben? Dieser Beitrag wird dann bei der Abrechnung wieder berücksichtigt.') : this.$t('Willst du den Beitrag wirklich archivieren? Archivierte Beiträge werden bei der Abrechnung nicht berücksichtigt.');
            showConfirm(this.$t('confirmInformal'), message, () => {
                axios
                    .patch('/club/member-club-fees/' + id + '/archived', { archived: !isArchived })
                    .then(() => this.loadMemberClubFees())
                    .catch(() => this.$notify.error('saveChanges.error'));
            }, buttonText, { width: 400 });
        },
        updateMemberClubFee(memberClubFee) {
            const error = new Error();
            if(!isValid(parse(memberClubFee.runTime.start, 'dd.MM.yyyy', new Date()))) {
                error.add('runTime', this.$t('Das Datum ist ungültig.'));
                this.error = error;
                return;
            }
            let payload = {
                'runTime': {
                    'start': this.getApiDateFromString(memberClubFee.runTime.start),
                },
                'note' : memberClubFee.note,
                'settleWithCredit' : memberClubFee.settleWithCredit,
            };
            if (memberClubFee.pricePerTime.type !== 'oneTime') {
                if(memberClubFee.runTime.end !== null && !isValid(parse(memberClubFee.runTime.end, 'dd.MM.yyyy', new Date()))) {
                    error.add('runTimeEnd', this.$t('Das Datum ist ungültig.'));
                    this.error = error;
                    return;
                }
                payload.runTime.end = this.getApiDateFromString(memberClubFee.runTime.end);
            }

            axios
                .put('/club/member-club-fees/' + memberClubFee.id, payload)
                .then(() => {
                    this.closeModals();
                    this.loadMemberClubFees();
                })
                .catch((error) => {
                    this.error = Error.fromResponse(error.response);
                    this.$notify.error('saveChanges.error');
                });
        },
        getApiDateFromString(string) {
            if (string === null) {
                return null;
            }

            let [day, month, year] = string.split('.');

            return {
                'day': parseInt(day),
                'month': parseInt(month),
                'year': parseInt(year),
            }
        },
        getDiscounts() {
            axios
                .get('/club/clubs/' + this.clubId + '/discounts')
                .then(response => {
                    this.discounts = response.data.data;
                    this.discounts.push({
                        id: 'individual',
                        name: this.$t('Individueller Preis'),
                    });
                });
        },
        formatDate(date) {
            return new Helper().formatDate(date);
        },
        initNew() {
            this.newMemberClubFee = {
                clubFee: null,
                note: '',
                invoiceRecipient: (this.defaultInvoiceReceiver !== null ? this.defaultInvoiceReceiver : this.id),
                discount: null,
                start: format(startOfMonth(new Date()), 'dd.MM.yyyy'),
                end: null,
                quantityFactor: 1,
                fixedPriceDiscount: {
                    amount: null,
                    netOrGross: 'gross'
                },
                paymentMethod: this.defaultPaymentMethodOfInvoiceReceiver,
                sepaDebit: null,
                chargeCredit: false,
                settleWithCredit: false
            };
        },
        getQuantityFactor(quantityFactor) {
            return quantityFactor.numerator / quantityFactor.denominator;
        },
        getPricePerTime(pricePerTime) {
            return new Helper().getPricePerTime(pricePerTime);
        },
        getDiscountedPricePerTime(pricePerTime, discountedPrice) {
            return new Helper().getDiscountedPricePerTime(pricePerTime, discountedPrice);
        },
        getPaymentMethod(item) {
            let str = new Helper().translatePaymentMethod(item.paymentMethod);
            return str;
        },
        openCreditDialog(memberClubFee) {
            this.setModalFee(memberClubFee);
            this.showCreditDialog = true;
        },
        openRuntimeDialog(memberClubFee) {
            this.setModalFee(memberClubFee);
            this.showRuntimeDialog = true;
        },
        openNoteDialog(memberClubFee) {
            this.setModalFee(memberClubFee);
            this.showNoteDialog = true;
        },
        openPaymentHistoryDialog(memberClubFee) {
            this.setModalFee(memberClubFee);
            this.showPaymentHistoryDialog = true;
        },
        openCreateDialog() {
            this.initNew();
            this.loadMemberSettings();
            this.getDiscounts();
            this.showCreateDialog = true;
        },
        closeCreateDialog() {
            this.showCreateDialog = false;
            this.newMemberClubFee = null;
        },
        closePaymentHistoryDialog() {
            this.showPaymentHistoryDialog = false;
            this.modalFee = null;
        },
        closeNoteDialog() {
            this.showNoteDialog = false;
            this.modalFee = null;
        },
        closeCreditDialog() {
            this.showCreditDialog = false;
            this.modalFee = null;
        },
        closeRuntimeDialog() {
            this.showRuntimeDialog = false;
            this.modalFee = null;
        },
        closeModals() {
            this.showCreditDialog = false;
            this.showRuntimeDialog = false;
            this.showNoteDialog = false;
            this.showCreateDialog = false;
            this.showPaymentHistoryDialog = false;
            this.modalFee = null;
            this.newMemberClubFee = null;
        },
        setModalFee(memberClubFee) {
            this.error = new Error();
            this.modalFee = JSON.parse(JSON.stringify(memberClubFee));
            const parsedStart = parseISO(this.modalFee.runTime.start);
            this.modalFee.runTime.start = isValid(parsedStart) ? format(parsedStart, 'dd.MM.yyyy') : null;
            if(this.modalFee.runTime.end) {
                const parsedEnd = parseISO(this.modalFee.runTime.end);
                this.modalFee.runTime.end = isValid(parsedEnd) ? format(parsedEnd, 'dd.MM.yyyy') : null;
            }
        },
        redirectToOverview() {
            window.location = `/backend/clubs/${this.clubId}/members`;
        }
    }
}
</script>
<style scoped lang="scss">
</style>
